/**
 * Storage is behind an interface so the document layer stays testable in Node
 * and free of browser APIs. `@bw/app` supplies the OPFS/IndexedDB
 * implementation; tests use `MemoryStorage`.
 */
export interface LibraryEntry {
  readonly id: string;
  readonly name: string;
  readonly savedAt: string;
  readonly brickCount: number;
}

export interface StorageAdapter {
  readAutosave(): Promise<string | null>;
  writeAutosave(text: string): Promise<void>;
  clearAutosave(): Promise<void>;
  listLibrary(): Promise<LibraryEntry[]>;
  putLibrary(entry: LibraryEntry, text: string): Promise<void>;
  getLibrary(id: string): Promise<string | null>;
  deleteLibrary(id: string): Promise<void>;
}

export class MemoryStorage implements StorageAdapter {
  #autosave: string | null = null;
  #library = new Map<string, { entry: LibraryEntry; text: string }>();

  async readAutosave(): Promise<string | null> {
    return this.#autosave;
  }

  async writeAutosave(text: string): Promise<void> {
    this.#autosave = text;
  }

  async clearAutosave(): Promise<void> {
    this.#autosave = null;
  }

  async listLibrary(): Promise<LibraryEntry[]> {
    return [...this.#library.values()]
      .map((row) => row.entry)
      .sort((a, b) => b.savedAt.localeCompare(a.savedAt));
  }

  async putLibrary(entry: LibraryEntry, text: string): Promise<void> {
    this.#library.set(entry.id, { entry, text });
  }

  async getLibrary(id: string): Promise<string | null> {
    return this.#library.get(id)?.text ?? null;
  }

  async deleteLibrary(id: string): Promise<void> {
    this.#library.delete(id);
  }
}

/**
 * Drives autosave: coalesces edits into one write after a quiet period, and
 * flushes immediately when asked (page hide, before a risky operation).
 */
export class Autosaver {
  #timer: ReturnType<typeof setTimeout> | null = null;
  #pending: (() => string) | null = null;
  #inFlight: Promise<void> = Promise.resolve();

  constructor(
    private readonly storage: StorageAdapter,
    private readonly quietMs = 5000,
  ) {}

  schedule(serialise: () => string): void {
    this.#pending = serialise;
    if (this.#timer !== null) clearTimeout(this.#timer);
    this.#timer = setTimeout(() => void this.flush(), this.quietMs);
  }

  async flush(): Promise<void> {
    if (this.#timer !== null) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
    const serialise = this.#pending;
    this.#pending = null;
    if (serialise === null) return this.#inFlight;
    const text = serialise();
    this.#inFlight = this.storage.writeAutosave(text).catch(() => undefined);
    return this.#inFlight;
  }

  cancel(): void {
    if (this.#timer !== null) clearTimeout(this.#timer);
    this.#timer = null;
    this.#pending = null;
  }
}
